import React, { useEffect, useState } from "react";
import DataGrid, { TextEditor, Column, RowRendererProps } from "react-data-grid";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchBulkProcessCollabData, processCollaborationMilestoneStep } from "services/api/campaign/collaboration";
import { fetchAddressThunk } from "slices/addressBook/thunk";
import { AppDispatch, RootState } from "store";
import { BulkProcessCollab } from "types/campaign/collaborations";
import Swal from "sweetalert2";
import { campaignIconSVG } from "pages/Campaigns/Campaign";
import { toast } from "react-toastify";
import { getErrorMessage, slugify } from "utils/helpers";
import { EligibleGigStep } from "pages/Campaigns/Collaborations/BulkProcess";
import { LoadingOverlay } from "Components/Common/LoadingOverlay";
import * as XLSX from "xlsx";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { dateFormats, dateParseFormats } from "constants/global";
import moment from "moment";

interface BulkProcessSheetProps {
    milestoneId: number;
    gigStep: EligibleGigStep;
}

/** Allow unpadded day/month when a specific format was chosen in the mapping modal. */
const preferredFormatVariants: Record<string, string[]> = {
    "YYYY-MM-DD": ["YYYY-MM-DD", "YYYY-M-D"],
    "DD-MM-YYYY": ["DD-MM-YYYY", "D-M-YYYY"],
    "DD-MM-YY": ["DD-MM-YY", "D-M-YY"],
    "MM-DD-YYYY": ["MM-DD-YYYY", "M-D-YYYY"],
    "MM-DD-YY": ["MM-DD-YY", "M-D-YY"],
    "YYYY/MM/DD": ["YYYY/MM/DD", "YYYY/M/D"],
    "DD/MM/YYYY": ["DD/MM/YYYY", "D/M/YYYY"],
    "DD/MM/YY": ["DD/MM/YY", "D/M/YY"],
    "MM/DD/YYYY": ["MM/DD/YYYY", "M/D/YYYY"],
    "MM/DD/YY": ["MM/DD/YY", "M/D/YY"],
    "DD MMM YYYY": ["DD MMM YYYY", "D MMM YYYY"],
    "MMM DD, YYYY": ["MMM DD, YYYY", "MMM D, YYYY"],
    "YYYY.MM.DD": ["YYYY.MM.DD", "YYYY.M.D"],
};

/**
 * Parse a date cell to ISO YYYY-MM-DD.
 * - If preferredFormat is set (upload mapping), only that pattern (plus unpadded variants) is accepted.
 * - Otherwise auto-detect across supported formats (day-first before month-first).
 */
const parseBulkDateToIso = (value: string, preferredFormat?: string): string | null => {
    console.log(value, preferredFormat);
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (preferredFormat) {
        const formats = preferredFormatVariants[preferredFormat] ?? [preferredFormat];
        const parsed = moment(trimmed, formats, true);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
    }

    const parsed = moment(trimmed, dateParseFormats, true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

interface Row {
    [key: string]: string;
}

interface ColumnDef {
    label: string;
    slug: string;
    filterable?: boolean;
    step?: string,
    editable?: boolean,
    isDate?: boolean,
    default?: boolean
}

interface RowError {
    rowIdx: number,
    colId: string,
    error: string
}

interface Step {
    slug: string,
    name: string
}

const defaultColumns: ColumnDef[] = [
    { slug: "name", label: "Name", default: true },
    { slug: "ig_username", label: "Username", default: true },
];
const BulkProcessSheet: React.FC<BulkProcessSheetProps> = ({
    milestoneId,
    gigStep
}) => {
    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const selectedAgencyId = useSelector(
        (state: RootState) => state.auth.selectedAgencyId
    );

    const [columns, setColumns] = useState<ColumnDef[]>(defaultColumns);
    const [rows, setRows] = useState<Row[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
    const [rawData, setRawData] = useState<BulkProcessCollab[]>([]);
    const [rowsErrors, setRowsErrors] = useState<RowError[]>([]);
    const [excludedSteps, setExcludedSteps] = useState<string[]>([]);
    const [completedSteps, setCompletedSteps] = useState<{ slug: string, name: string }[]>([]);
    const [rowStatus, setRowStatus] = useState<{ [username: string]: { status: 'success' | 'error', message?: string } }>({});


    const [processing, setProcessing] = useState(false);
    const [overlay, setOverlay] = useState<{ visible: boolean, text?: string }>({ visible: false });
    const [fetching, setFetching] = useState(true);


    // For mapping modal
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [uploadedHeaders, setUploadedHeaders] = useState<string[]>([]);
    const [tempRows, setTempRows] = useState<Row[]>([]);
    const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
    const [dateFormatMapping, setDateFormatMapping] = useState<Record<string, string>>({});

    const [primaryKey, setPrimaryKey] = useState<string>('');
    const [selectedCell, setSelectedCell] = useState<{ rowIdx: number; colIdx: number } | null>(null);

    const hasError = (rowIdx: number, colId: string) => {
        return rowsErrors.some(err => err.rowIdx === rowIdx && err.colId === colId);
    };

    const getError = (rowIdx: number, colId: string) => {
        return rowsErrors.find(err => err.rowIdx === rowIdx && err.colId === colId);
    };



    const getCombinedAddress = (address: any | undefined | null) => {
        if (address) {
            const combinedAddress = `${address.premise ? address.premise + "," : ""} ${address.sublocality ? address.sublocality + "," : ""
                } ${address.city ? address.city + "," : ""} ${address.region ? address.region + "," : ""
                } ${address.pincode}`;
            return combinedAddress;
        }
        return "*";
    };

    const parseRows = async (data: BulkProcessCollab[]) => {
        const campaignGigSlug = slugify(gigStep.gig_type.object_name);
        const campaignGigObjName = gigStep.gig_type.object_name;
        let parsed_rows: Row[] = [];
        let sheetColumns: ColumnDef[] = [...defaultColumns, { slug: campaignGigSlug, label: campaignGigObjName }];
        let completed_steps: Step[] = [];
        if (Array.isArray(data)) {
            for (let r of data) {
                if (!r.social_account?.handle || !r.social_account?.name) continue;
                let parsedRow: Row = {
                    name: r.influencer?.name ?? r.social_account?.name ?? `@${r.social_account?.handle}`,
                    ig_username: r.social_account.handle,
                    [campaignGigSlug]: (Array.isArray(r.campaign_gigs) ? r.campaign_gigs : [])
                        .map((cg) => `${cg.name} ${Array.isArray(cg.options) ? cg.options.map((opt) => `${opt.label}: ${opt.values[0]}`).join(', ') : ''}`)
                        .join(", ")
                };

                const influencerProcessedSteps = r.processed_steps.filter((step) =>
                    (step.assignees ?? []).includes("influencer")
                );
                for (let step of influencerProcessedSteps) {
                    const deliverables = step.deliverables;
                    const keys = Object.keys(deliverables ?? {});
                    for (let key of keys) {
                        const property = step.gig_step.properties.find(
                            (pr) => pr.key === key
                        );
                        if (!property) continue;
                        let value = (deliverables ?? {})[key] ?? "";
                        if (property && property.type === "address" && value) {
                            const { address } = await dispatch(
                                fetchAddressThunk({ addressUUID: value })
                            ).unwrap();
                            value = getCombinedAddress(address);
                        }
                        const columnSlug = `${step.gig_step.slug}.${key}`;
                        const columnLabel = `${property.label} (${step.gig_step.name})`;
                        parsedRow[columnSlug] = value;
                        const sheetColumn = sheetColumns.find(
                            (sc) => sc.slug === columnSlug
                        );
                        if (!sheetColumn) {
                            sheetColumns.push({ label: columnLabel, slug: columnSlug, filterable: true, step: step.gig_step.slug });
                        }
                    }
                    const completedStep = completed_steps.find((s) => s.slug === step.gig_step.slug);
                    if (!completedStep && step.gig_step.slug !== 'gig_selection') {
                        completed_steps.push({ slug: step.gig_step.slug, name: step.gig_step.name });
                    }
                }


                const stepToBeProcessed = r.pending_steps.find(
                    (step) => step.gig_step.slug === gigStep.slug
                );

                if (stepToBeProcessed) {
                    if (Array.isArray(stepToBeProcessed.gig_step.properties)) {
                        for (let property of stepToBeProcessed.gig_step.properties) {
                            const columnSlug = `${stepToBeProcessed.gig_step.slug}.${property.key}`;
                            const columnLabel = `${property.label} (${stepToBeProcessed.gig_step.name})`;
                            parsedRow[columnSlug] = "";
                            const sheetColumn = sheetColumns.find(
                                (sc) => sc.slug === columnSlug
                            );
                            if (!sheetColumn) {
                                sheetColumns.push({ label: columnLabel, slug: columnSlug, editable: true, isDate: property.type === 'date' });
                            }
                        }
                    }
                }

                parsed_rows.push(parsedRow);
            }
        }
        return [parsed_rows, sheetColumns, completed_steps] as [Row[], ColumnDef[], Step[]];
    };

    const fetchCollabsData = async () => {
        try {
            setFetching(true);
            if (!selectedAgencyId || !campaignId || !milestoneId || !gigStep) return;
            const data: BulkProcessCollab[] = await fetchBulkProcessCollabData(
                selectedAgencyId,
                campaignId,
                milestoneId,
                gigStep.slug
            );
            setRawData(data);
            const [parsedRows, sheetColumns, completed_steps] = await parseRows(data);
            setRows(parsedRows);
            setColumns(sheetColumns);
            setCompletedSteps(completed_steps);
        } catch (error) {
            console.log(error);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (campaignId && selectedAgencyId && milestoneId && gigStep) {
            fetchCollabsData();
        }
    }, [campaignId, selectedAgencyId, milestoneId, gigStep]);

    // --- Selection handlers ---
    const toggleRow = (rowIdx: number) => {
        const newSet = new Set(selectedRowIds);
        if (newSet.has(rowIdx)) newSet.delete(rowIdx);
        else newSet.add(rowIdx);
        setSelectedRowIds(newSet);
    };

    const toggleAll = () => {
        if (selectedRowIds.size === rows.length) {
            setSelectedRowIds(new Set());
        } else {
            setSelectedRowIds(new Set(rows.map((_, idx) => idx)));
        }
    };

    // Export CSV (only selected if any, else all)
    const exportCSV = () => {
        const dataToExport =
            selectedRowIds.size > 0
                ? rows.filter((_, idx) => selectedRowIds.has(idx))
                : rows;
        // Remove excluded columns from export
        const exportableColumns = columns.filter(col => (!col.step || !excludedSteps.includes(col.step))).map(col => col.slug);
        const filteredDataToExport = dataToExport.map(row => {
            const filteredRow: any = {};
            exportableColumns.forEach(col => {
                filteredRow[col] = row[col];
            });
            return filteredRow;
        });

        const csv = Papa.unparse(filteredDataToExport);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "bulk-process.csv");
    };

    // Import CSV
    const importCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setOverlay({ visible: true, text: 'Importing...' })
        const reader = new FileReader();
        const fileExt = file.name.split('.').pop()?.toLowerCase();

        if (fileExt === "csv") {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results: any) => {
                    const uploadedRows = results.data as Row[];
                    const headers = Object.keys(uploadedRows[0] || {});
                    setUploadedHeaders(headers);
                    setTempRows(uploadedRows);
                    setOverlay({ visible: false });
                    setShowMappingModal(true);
                }
            });
        } else if (fileExt === "xlsx" || fileExt === "xls") {
            reader.onload = (e) => {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: Row[] = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
                const headers = Object.keys(jsonData[0] || {});
                setUploadedHeaders(headers);
                setTempRows(jsonData);
                setOverlay({ visible: false });
                setShowMappingModal(true);
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert("Unsupported file type. Please upload a CSV or Excel file.");
            setOverlay({ visible: false });
        }
    };

    // Map columns for DataGrid
    const dataGridColumns: Column<Row>[] =
        columns.length > 0
            ? [
                {
                    key: "select",
                    name: (
                        <input
                            type="checkbox"
                            checked={selectedRowIds.size === rows.length && rows.length > 0}
                            onChange={toggleAll}
                        />
                    ),
                    width: 50,
                    frozen: true,
                    formatter: ({ rowIdx }) => {
                        const row = rows[rowIdx];
                        const rStatus = rowStatus[row.ig_username]?.status;
                        const rowError = rowStatus[row.ig_username]?.message;
                        return (
                            <div className="d-flex align-items-center h-100 gap-2 w-100" data-toggle='tooltip' title={rowError}>
                                <input
                                    type="checkbox"
                                    disabled={rStatus === 'success'}
                                    checked={selectedRowIds.has(rowIdx)}
                                    onChange={() => toggleRow(rowIdx)}
                                />
                                {
                                    rStatus === 'error' && rowError &&
                                    <span className="text-danger cursor-pointer" data-toggle='tooltip' title={rowError}><i className="ri-information-fill fs-5"></i></span>
                                }
                            </div>
                        )
                    }
                },
                ...columns.filter((col) => (!col.step || !excludedSteps.includes(col.step))).map((col) => ({
                    key: col.slug,
                    name: col.label,
                    editor: col.editable ? TextEditor : undefined,
                    renderCell: ({ row }: { row: Row }) => {
                        return (
                            <div title={rowStatus[row.ig_username]?.message ?? ''} className="hello">
                                {row[col.slug]}
                            </div>)
                    },
                    cellClass: (row: Row) => (hasError(rows.indexOf(row), col.slug) ? 'error-cell' : ''),
                }))
            ]
            : [];

    const selectedColKey =
        selectedCell && dataGridColumns[selectedCell.colIdx]
            ? String(dataGridColumns[selectedCell.colIdx].key)
            : null;
    const selectedCellError =
        selectedCell && selectedColKey && selectedColKey !== "select"
            ? getError(selectedCell.rowIdx, selectedColKey)
            : undefined;
    const selectedCellColumnLabel = selectedColKey
        ? columns.find((c) => c.slug === selectedColKey)?.label ?? selectedColKey
        : "";
    const selectedCellUsername =
        selectedCell && rows[selectedCell.rowIdx]
            ? rows[selectedCell.rowIdx].ig_username
            : "";

    useEffect(() => {
        setExcludedSteps(completedSteps.map((step) => step.slug));
    }, [completedSteps])

    const processSelectedRows = async () => {
        if (selectedRowIds.size === 0 || !campaignId || !selectedAgencyId || processing) return;

        const result = await Swal.fire({
            title: "Are you sure you want to process selected collaborations?",
            text: "We hope that you have filled all the data correctly",
            iconHtml: campaignIconSVG,
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Yes, Process",
            reverseButtons: true
        });

        if (!result || !result.isConfirmed) return;

        try {
            setProcessing(true);
            setOverlay({ visible: true, text: 'Processing...' })
            const rowsErrors: RowError[] = [];

            const rowsToProcess: {
                collaborationId: string,
                ig_username: string,
                data: any
            }[] = [];

            Array.from(selectedRowIds).forEach((rowIdx) => {
                const row = rows[rowIdx];
                const rawRowData = rawData.find((r) => r?.social_account?.handle === row.ig_username);
                if (!rawRowData || !rawRowData.collaboration?.uuid) return;
                const stepToBeProcessed = rawRowData.pending_steps.find((step) => step.gig_step.slug === gigStep.slug);
                if (!stepToBeProcessed) return;
                const properties = stepToBeProcessed.gig_step.properties;
                const rowToProcess = {
                    collaborationId: rawRowData.collaboration.uuid,
                    ig_username: rawRowData.social_account.handle,
                    data: {}
                }
                for (let property of properties) {
                    const key = `${stepToBeProcessed.gig_step.slug}.${property.key}`
                    const value = row[key] ? row[key].trim() : '';

                    if (property.mandatory && !value) {
                        rowsErrors.push({
                            rowIdx,
                            colId: key,
                            error: `${property.label} is mandatory`
                        });
                        continue;
                    }
                    if (property.type === 'date') {
                        if (!value) {
                            rowToProcess.data = { ...rowToProcess.data, [property.key]: value };
                            continue;
                        }

                        const selectedDateFormat = dateFormatMapping[key] || undefined;
                        const formattedValue = parseBulkDateToIso(value, selectedDateFormat);
                        if (!formattedValue) {
                            const formatHint = selectedDateFormat
                                ? dateFormats.find((f) => f.format === selectedDateFormat)?.label ?? selectedDateFormat
                                : "YYYY-MM-DD, DD/MM/YYYY, DD/MM/YY, etc.";
                            rowsErrors.push({
                                rowIdx,
                                colId: key,
                                error: `${property.label} must be a valid date (${formatHint})`
                            });
                            continue;
                        }
                        rowToProcess.data = { ...rowToProcess.data, [property.key]: formattedValue };
                        continue;
                    }
                    rowToProcess.data = { ...rowToProcess.data, [property.key]: value }
                }
                rowsToProcess.push(rowToProcess);
            });

            setRowsErrors(rowsErrors);
            if (rowsErrors.length > 0) {
                toast.error("Please fill valid information in highlighted cells to process!");
                return;
            }

            for (let row of rowsToProcess) {
                try {
                    await processCollaborationMilestoneStep(selectedAgencyId, campaignId, row.collaborationId, String(milestoneId), { step: gigStep.slug, ...row.data });
                    setRowStatus(prev => ({
                        ...prev,
                        [row.ig_username]: { status: 'success' }
                    }));

                } catch (error: any) {
                    console.log(error)
                    setRowStatus(prev => ({
                        ...prev,
                        [row.ig_username]: {
                            status: 'error',
                            message: error?.response?.data?.message ?? "Processing Failed!",
                        }
                    }));
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setProcessing(false);
            setOverlay({ visible: false })
        }

    }

    useEffect(() => {
        const deselect_rows: string[] = [];
        for (let [ig_username, row] of Object.entries(rowStatus)) {
            if (row.status === 'success') {
                deselect_rows.push(ig_username);
            }
        }

        setSelectedRowIds((prev) => {
            // Filter out rows whose ig_username is in deselect_rows
            const newSet = new Set<number>();
            rows.forEach((row, idx) => {
                if (!deselect_rows.includes(row.ig_username) && prev.has(idx)) {
                    newSet.add(idx);
                }
            });
            return newSet;
        });

    }, [rowStatus])

    const applyMapping = () => {
        if (!tempRows.length || !Object.keys(columnMapping).length || !primaryKey) return;

        const editableColumns = columns.filter((col) =>  col.editable);
        let updatedRows = [...rows];
        for(let i=0; i <= rows.length; i++){
            let row = {...updatedRows[i]};
            if(!row[primaryKey]){
                continue;
            }

            const uploadedRow = tempRows.findLast((r) => r[primaryKey] && row[primaryKey] && r[primaryKey] === row[primaryKey])

            if(uploadedRow){
                for(let editableCol of editableColumns){
                    row[editableCol.slug] = uploadedRow[editableCol.slug] ?? '';
                }
            }
            updatedRows[i] = row;
        }

        // Replace rows with mapped data
        setRows(updatedRows);

        // Reset modal state
        setShowMappingModal(false);
        setColumnMapping({});
        setUploadedHeaders([]);
        setTempRows([]);
        setPrimaryKey('');

    };


    return (
        <div className="rdg-light bulk-process-sheet-wrapper" style={{ height: "calc(100vh - 320px)" }}>
            <LoadingOverlay text={overlay.text} visible={overlay.visible ?? false} />
            {
                fetching ? <div className="page-content">
                    <div className="loader-wrapper h-auto">
                        <div className="loader"></div>
                    </div>
                </div> : (
                    rows.length > 0 ? (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-shrink-0">
                                <h6 className="m-0">Filter Deliverables: </h6>
                                <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                                    {
                                        completedSteps.map((step) => {
                                            return (
                                                <div className="form-check">
                                                    <input className="form-check-input" type="checkbox" id={step.slug} checked={!excludedSteps.includes(step.slug)} onChange={(e) => {
                                                        let excluded_steps = [...excludedSteps];
                                                        if (excluded_steps.includes(step.slug)) {
                                                            excluded_steps = excluded_steps.filter((c) => c !== step.slug);
                                                        } else {
                                                            excluded_steps.push(step.slug);
                                                        }
                                                        setExcludedSteps(excluded_steps);
                                                    }} />
                                                    <label className="form-check-label" htmlFor={step.slug}>
                                                        {step.name}
                                                    </label>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                            <DataGrid
                                className="rdg-light bulk-process-sheet"
                                columns={dataGridColumns}
                                rows={rows}
                                onRowsChange={setRows}
                                headerRowHeight={50}
                                rowKeyGetter={(row: Row) => row.ig_username}
                                rowClass={(row) => rowStatus[row.ig_username]?.status === 'error' ? 'row-danger' : (rowStatus[row.ig_username]?.status === 'success' ? 'row-success' : '')}
                                onSelectedCellChange={({ idx, rowIdx }) => {
                                    setSelectedCell({ rowIdx, colIdx: idx });
                                }}
                            />
                            <div className="bulk-process-sheet-footer">
                                {selectedCellError ? (
                                    <div className="text-danger small mt-2 d-flex align-items-start gap-1">
                                        <span>
                                            {selectedCellUsername ? <strong>@{selectedCellUsername}</strong> : null}
                                            {selectedCellUsername && selectedCellColumnLabel ? " · " : null}
                                            {selectedCellColumnLabel ? <strong>{selectedCellColumnLabel}</strong> : null}
                                            {(selectedCellUsername || selectedCellColumnLabel) ? ": " : null}
                                            {selectedCellError.error}
                                        </span>
                                    </div>
                                ) : rowsErrors.length > 0 ? (
                                    <div className="text-muted small mt-2">
                                        {rowsErrors.length} cell{rowsErrors.length === 1 ? "" : "s"} have errors - click a highlighted cell to see details.
                                    </div>
                                ) : null}
                                <div className="d-flex gap-2 justify-content-between align-items-center mt-3 flex-column flex-md-row">
                                    <div className="d-flex gap-2 justify-content-start align-items-center w-100">
                                        <button className="btn btn-soft-primary w-25" onClick={exportCSV}>Export CSV</button>
                                        <input
                                            className="form-control w-50"
                                            placeholder="Import Sheet"
                                            type="file"
                                            accept=".csv, .xls, .xlsx"
                                            onChange={importCSV}
                                            style={{ marginLeft: "10px" }}
                                        />
                                    </div>
                                    <div className="d-flex gap-3 justify-content-end align-items-center w-100">
                                        <span>
                                            <strong>Selected Rows:</strong>{" "}
                                            {selectedRowIds.size}
                                        </span>
                                        <button disabled={processing || !selectedRowIds.size} className="btn btn-primary px-5" onClick={() => processSelectedRows()}>
                                            {processing ? (<span className="d-flex align-items-center gap-2"><div style={{ height: '16px', width: '16px' }} className="spinner-border text-light" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div> Processing...</span>) : 'Process'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* Mapping Modal */}
                            <Modal scrollable isOpen={showMappingModal} toggle={() => setShowMappingModal(false)} centered>
                                <ModalHeader toggle={() => setShowMappingModal(false)}>
                                    Map Columns
                                </ModalHeader>
                                <ModalBody className="pt-0">
                                    <div className="form-group filter-item p-3">
                                        <div className="d-flex flex-column">
                                            <label className="form-label mb-0">Unique Column</label>
                                            <span className="text-muted" style={{fontSize: '12px'}}>Select a column that will uniquely identify the row.</span>
                                        </div>

                                        <select
                                            className="form-select"
                                            value={primaryKey}
                                            onChange={(e) => setPrimaryKey(e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            {
                                                columns.filter((col) => col.default).map((col) => <option value={col.slug}>{col.label}</option>)
                                            }
                                        </select>
                                    </div>
                                    <div className="divider my-3"></div>
                                    <div className="d-flex flex-column gap-2">
                                        {columns.filter((col) => ((col.default && col.slug === primaryKey) || col.editable)).map(col => (
                                            <div key={col.slug} className="d-flex flex-column gap-2 filter-item p-3">
                                                <div className="w-100">
                                                    <label className="form-label">{col.label}</label>
                                                    <select
                                                        className="form-select"
                                                        value={columnMapping[col.slug] || ""}
                                                        onChange={e => setColumnMapping({ ...columnMapping, [col.slug]: e.target.value })}
                                                    >
                                                        <option value="">-- Select column --</option>
                                                        {uploadedHeaders.map(h => (
                                                            <option key={h} value={h}>{h}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {
                                                    col.isDate &&
                                                    <div className="w-100">
                                                        <label className="form-label">Format</label>
                                                        <select
                                                            className="form-select"
                                                            value={dateFormatMapping[col.slug] ?? ''}
                                                            onChange={(e) => setDateFormatMapping({ ...dateFormatMapping, [col.slug]: e.target.value })}
                                                        >
                                                            <option value="">Select</option>
                                                            {
                                                                dateFormats.map((format, idx) => <option value={format.format} key={idx}>{format.label}</option>)
                                                            }
                                                        </select>
                                                    </div>
                                                }
                                            </div>
                                        ))}
                                    </div>
                                </ModalBody>
                                <ModalFooter>
                                    <button
                                        className="btn btn-gradient-outline"
                                        onClick={() => setShowMappingModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-gradient"
                                        disabled={(columns
                                            .filter(col => ((col.default && col.slug === primaryKey) || col.editable)) // only exportable
                                            .some(col => !columnMapping[col.slug]) ||
                                            columns
                                                .filter(col => (((col.default && col.slug === primaryKey) || col.editable) && col.isDate))
                                                .some(col => !dateFormatMapping[col.slug]) ||
                                            !primaryKey)
                                        }
                                        onClick={applyMapping}
                                    >
                                        Apply
                                    </button>
                                </ModalFooter>
                            </Modal>
                        </>
                    ) : (
                        <div className="d-flex w-100 justify-content-center align-items-center">
                            <div className="length-zero">
                                <h3>There are no collaborations to process with this pending step!</h3>
                            </div>
                        </div>
                    )

                )
            }


        </div>
    );
};

export default BulkProcessSheet;
